import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

interface Item {
    [key: string]: unknown;
    id: string;
    name: string;
}

interface SortableList<T extends Item = Item> {
    items: T[];
    renderItem(item: T): JSX.Element;
    renderSortableItem(item: T): JSX.Element;
    onUpdated?(items: T[]): void;
}

const SortableList = <T extends Item>({ items, renderSortableItem, renderItem, onUpdated }: SortableList<T>) => {
    const [activeItem, setActiveItem] = useState<T | null>(null);
    const sensors = useSensors(
        useSensor(TouchSensor),
        useSensor(MouseSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = ({ active }: DragStartEvent) => {
        const item = items.find(({ id }) => id === active.id);

        item && setActiveItem(item);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = items.findIndex(({ id }) => id === active.id);
            const newIndex = items.findIndex(({ id }) => id === over?.id);

            const updated = arrayMove(items, oldIndex, newIndex);

            onUpdated?.(updated);
        }

        setActiveItem(null);
    };

    const handleDragCancel = () => setActiveItem(null);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <SortableContext items={items.map(({ id }) => id)} strategy={verticalListSortingStrategy}>
                {items.map((item) => (
                    <React.Fragment key={item.id}>{renderSortableItem(item)}</React.Fragment>
                ))}
            </SortableContext>
            {createPortal(<DragOverlay style={{ position: 'relative' }}>{activeItem ? renderItem(activeItem) : null}</DragOverlay>, document.body)}
        </DndContext>
    );
};

export default SortableList;
export { Item };
